import { RootState } from "common/reducers";
import { createSelector } from "reselect";
import { getRecordsByModel } from "common/selectors/idsManagement.selectors";
import { DateFormat } from "common/helpers/Format";
import { JobRecord, validMetadataIndices } from "common/records/Job";
import { DocumentRecord, IDocumentData } from "common/records/Document";
import { List } from "immutable";
import { Nullable } from "common/types";

export const rootKey = "jobs";

/**
 * Gets the job ID currently set as 'current'
 *
 * @param {RootState} state The RootState
 * @param {{}} props
 * @returns number
 */
export const currentJobId = (state: RootState, _props?: any) =>
  state.getIn([rootKey, "currentJobId"]) as Nullable<number>;

/**
 * Loads the current job using currentJobId
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {JobRecord} JobRecord
 */
export const currentJob = (state: RootState, _props?: any) => {
  const id = currentJobId(state);
  if (!id) {
    return null;
  }
  return state.getIn([rootKey, "byId"]).get(id);
};

/**
 * Loads the current job name
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {string} string
 */
export const currentJobName = (state: RootState) => currentJob(state)?.get("name");

/**
 * Loads the current job measurement_id
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {string} string
 */
export const currentJobMeasurementId = (state: RootState) => currentJob(state)?.get("measurement_id");

/**
 * Loads the current job errors
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {string} string
 */
export const currentJobErrors = (state: RootState) => currentJob(state)?.get("errors");

/**
 * Loads the list of sample jobs
 */
export const sampleJobs = (state: RootState) => state.getIn([rootKey, "sampleJobs"]);

/**
 * Loads the resource documents list for the current job
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {List<any>} List of records
 */
export const resourceDocuments = (state: RootState, _props?: any) =>
  state.getIn(["jobs", "byId", currentJobId(state), "config", "resource_documents"], List()) as List<any>;

/**
 * Loads the resource documents ids list for the current job
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {List<number>} List of ids
 */
export const jobResourceIds = createSelector([resourceDocuments], (documents) =>
  documents.map((doc: DocumentRecord) => doc.get("id")),
);

/**
 * Loads the current job using currentJobId and returns job.loading
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {boolean} loading
 */
export const currentJobLoading = createSelector([currentJob], (job) => {
  if (!job) return true;

  return job.loading;
});

/**
 * Loads the list of job overviews using the idsManagement `getRecordsByModel` selector
 */
export const jobOverviews = (state: RootState) =>
  getRecordsByModel(state, {
    modelName: "jobs",
    path: ["jobs", "overviewsById"],
    includeArchived: true,
  });

/**
 * Gets the color for the given job state id
 */
export const jobStateColor = (state: RootState, id: string) =>
  state
    .getIn([rootKey, "jobStates"])
    .find((state) => state.get("id") === id)
    ?.get("color");

/**
 * Loads the current jobParams used for listJobs
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {boolean} loading
 */
export const jobParams = (state: RootState, jobParamsType: "jobParams" | "orgJobParams") =>
  state.getIn([rootKey, jobParamsType]);

/**
 * Loads the list of job overviews using the idsManagement `getRecordsByModel` selector
 */
export const jobOverviewById = (state: RootState, jobId: number) => state.getIn([rootKey, "overviewsById"]).get(jobId);

/**
 * Gets the start time for the upcoming appointment
 * @param {RootState} state The RootState
 * @param {{jobId: number}} options
 * @returns {string} No Appt. or formatted start time
 */

export const nextAppointment = (state: RootState, jobId: number) => {
  const formattedStartTime = DateFormat(jobOverviewById(state, jobId)?.appointments.first()?.get("start_time"));

  if (formattedStartTime === "Invalid date") return "No Appt.";

  return formattedStartTime;
};

/**
 * Gets the name of the user to whom the job is assigned
 * @param {RootState} state The RootState
 * @param {{jobId: number}} options
 * @returns {string} Full name of user
 */

export const assignedTo = (state: RootState, jobId: number) => {
  const assignedUser = jobOverviewById(state, jobId)?.assignments.first()?.user;

  if (assignedUser) return assignedUser.get("first_name") + " " + assignedUser.get("last_name");

  return "";
};

/**
 * The user id assigned to the current job (its first assignment), or undefined when unassigned.
 * The Commission Calculator computes commission from THIS user's access config (overhead /
 * commission_rate), not the current user's (RSF-25820 BR-6).
 *
 * @param {RootState} state The RootState
 * @returns {number | undefined} the assigned user's id, or undefined when unassigned
 */
export const jobAssignedUserId = (state: RootState): number | undefined =>
  currentJob(state)?.assignments?.get(0)?.user_id;

/**
 * Loads the current job's root folder id by name
 *
 * @param {RootState} state The RootState
 * @param {{ folderName }} options
 * @returns {number} number
 */
export const currentJobRootFolderId = createSelector([currentJob, (_state, props) => props], (job, props) => {
  return job?.getIn(["folders", props.folderName]);
});

/**
 * Checks if job has integration key and value set in its metadata
 * @param {{job: JobRecord, integrationKey: string}}
 * @returns boolean value for its existence
 */
export const hasIntegration = (job: JobRecord, integrationKey: string): boolean => {
  const metadata = job?.metadata;

  if (!metadata) return false;

  return validMetadataIndices.some(
    (idx) =>
      metadata.get("integration_key_" + idx)?.includes(integrationKey) &&
      metadata.get("integration_value_" + idx) !== undefined,
  );
};
